import { Link } from "react-router-dom";
import { Calendar, MapPin, Clock, Ticket, ArrowUpRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useUpcomingEvents } from "@/hooks/useUpcomingEvents";
import { Reveal, RevealStagger, RevealItem } from "@/components/shared/Reveal";
import { ChapterHeading } from "@/components/shared/ChapterHeading";
import { IconTile } from "@/components/shared/IconTile";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { UPCOMING_EDITION, DISCOVERY_CATEGORIES, EDITION_HIGHLIGHTS, FAQ_UPCOMING, CONTACT_INFO, COLORS, UPCOMING_BANNER } from "@/data/siteData";

export default function UpcomingMarket() {
  const { dates, dateRange, venueName } = useUpcomingEvents();
  useSEO(
    `${UPCOMING_EDITION.name} — Upcoming Markets | The Market Place Jaipur`,
    `${UPCOMING_EDITION.timing}. ${UPCOMING_EDITION.teaser}`
  );

  const details = [
    { icon: Calendar, label: "Date", value: dateRange, sub: dates.join(" · ") },
    { icon: MapPin, label: "Venue", value: venueName },
    { icon: Clock, label: "Timings", value: UPCOMING_EDITION.timing },
    { icon: Ticket, label: "Entry", value: UPCOMING_EDITION.entry },
  ];

  return (
    <div data-testid="upcoming-market-page">
      <section className="pt-16 pb-16 px-6 sm:px-8 max-w-5xl mx-auto text-center">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#D81B60]">Upcoming Markets</span>
          <h1 className="font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">The Next Market Is Taking Shape.</h1>
          <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            A new mix of homegrown businesses. A familiar community. Another reason to discover what Jaipur is creating.
          </p>
        </Reveal>
      </section>

      {/* Event Details */}
      <section className="pb-16 px-6 sm:px-8 max-w-6xl mx-auto">
        <RevealStagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {details.map((d, i) => (
            <RevealItem key={d.label}>
              <div data-testid={`event-detail-${d.label.toLowerCase()}`} className="rounded-2xl border border-gray-200 p-6 h-full">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${COLORS[i % COLORS.length]}18` }}>
                  <d.icon size={22} style={{ color: COLORS[i % COLORS.length] }} strokeWidth={1.75} />
                </div>
                <p className="font-mono text-xs uppercase tracking-[0.15em] text-gray-400">{d.label}</p>
                <p className="font-bold text-lg mt-1">{d.value}</p>
                {d.sub && <p className="text-xs text-gray-400 mt-1">{d.sub}</p>}
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
        <Reveal>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/book-a-stall" data-testid="upcoming-apply-stall-button" className="rounded-full bg-[#D81B60] text-white px-8 py-4 text-sm font-semibold hover:bg-[#AD144B] transition-colors duration-300">
              Book a Stall
            </Link>
            <a href={CONTACT_INFO.instagram} target="_blank" rel="noopener noreferrer" data-testid="upcoming-save-date-button" className="rounded-full border border-gray-300 px-8 py-4 text-sm font-semibold hover:border-[#1A1A1A] transition-colors duration-300">
              Save the Date
            </a>
          </div>
        </Reveal>
      </section>

      {/* Photo banner */}
      <Reveal>
        <section className="px-6 sm:px-8 max-w-7xl mx-auto mb-16">
          <div className="rounded-3xl overflow-hidden">
            <img src={UPCOMING_BANNER} alt="Visitors exploring The Market Place, Jaipur" className="w-full h-[40vh] sm:h-[50vh] object-cover object-center" />
          </div>
        </section>
      </Reveal>

      {/* Edition Highlights */}
      <section className="pb-12 sm:pb-16 px-6 sm:px-8 max-w-6xl mx-auto">
        <ChapterHeading number="00" title="This Edition Brings" subtitle="Beyond the stalls, there's a lot more happening on the ground." />
        <RevealStagger className="grid grid-cols-2 sm:grid-cols-4 gap-5">
          {EDITION_HIGHLIGHTS.map((h, i) => (
            <RevealItem key={h.title}>
              <div data-testid={`edition-highlight-${i}`} className="rounded-2xl border border-gray-200 p-6 h-full text-center hover:shadow-md transition-shadow duration-300">
                <IconTile icon={h.icon} title={h.title} color={COLORS[i % COLORS.length]} center />
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
      </section>

      {/* What to Expect */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto">
          <ChapterHeading number="01" title="What to Expect" subtitle="The Market Place brings together businesses across categories, giving visitors the opportunity to discover products, meet founders and shop directly from independent businesses." />
          <RevealStagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {DISCOVERY_CATEGORIES.map((c, i) => (
              <RevealItem key={c.title}>
                <div className="rounded-xl border border-gray-200 bg-white p-5 text-center h-full hover:shadow-md transition-shadow duration-300">
                  <IconTile icon={c.icon} title={c.title} color={COLORS[i % COLORS.length]} center />
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
          <Reveal><p className="text-center mt-10 text-gray-500 italic">Every edition is different because the businesses behind it are different.</p></Reveal>
        </div>
      </section>

      {/* Meet the Participating Brands */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 max-w-4xl mx-auto text-center">
        <Reveal>
          <ChapterHeading number="02" title="Meet the Participating Brands" subtitle="Discover the businesses joining this edition of The Market Place." center />
          <div className="rounded-2xl border border-dashed border-gray-300 p-10 -mt-6">
            <p className="text-gray-500">Participating brands — with Brand Name, Instagram Handle, Stall Number, Category and Product Highlights — will be announced here closer to the market date.</p>
            <a
              href={CONTACT_INFO.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="explore-participating-brands-button"
              className="inline-flex items-center gap-2 mt-6 rounded-full bg-[#1A1A1A] text-white px-6 py-3 text-sm font-semibold hover:bg-[#D81B60] transition-colors duration-300"
            >
              Explore Participating Brands <ArrowUpRight size={16} />
            </a>
          </div>
        </Reveal>
      </section>

      {/* Plan Your Visit */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 bg-[#FDFBF7] text-center">
        <div className="max-w-4xl mx-auto">
        <Reveal>
          <ChapterHeading number="03" title="Plan Your Visit" subtitle="Come to shop, discover, meet the people behind the products and spend time within a community built around Jaipur's homegrown businesses." center />
          <a
            href={UPCOMING_EDITION.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="get-directions-button"
            className="inline-flex items-center gap-2 -mt-6 rounded-full border border-gray-300 px-6 py-3 text-sm font-semibold hover:border-[#1A1A1A] transition-colors duration-300"
          >
            Get Directions <ArrowUpRight size={16} />
          </a>
        </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 max-w-3xl mx-auto">
        <ChapterHeading number="04" title="FAQ" />
        <Accordion type="single" collapsible data-testid="upcoming-faq-accordion">
          {FAQ_UPCOMING.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="font-bold text-lg text-left">{f.q}</AccordionTrigger>
              <AccordionContent className="text-gray-500">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* For Businesses */}
      <section className="pb-16 px-6 sm:px-8 max-w-4xl mx-auto">
        <Reveal>
          <div className="rounded-3xl bg-gradient-to-br from-[#1A1A1A] to-[#2C2C2C] text-white p-10 sm:p-14 text-center">
            <h2 className="font-extrabold text-3xl sm:text-4xl mb-4">Want to Be Part of This Edition?</h2>
            <p className="text-white/70 max-w-xl mx-auto leading-relaxed">
              If you are building something of your own, The Market Place could be where more people discover it. We
              particularly welcome homegrown makers, first-time entrepreneurs, organic and sustainable businesses,
              and independent concepts with something meaningful to bring to the community.
            </p>
            <Link to="/book-a-stall" data-testid="upcoming-for-businesses-apply-button" className="inline-block mt-8 rounded-full bg-[#D81B60] text-white px-8 py-4 text-sm font-semibold hover:bg-[#F06292] transition-colors duration-300">
              Book a Stall
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
